import React from 'react';
import { useEffect, useState } from 'react';
import {
  Button,
  Group,
  Stack,
  Text,
  TextInput,
  Divider,
  Flex,
  UnstyledButton,
  Popover,
} from '@mantine/core';
import { DateTimePicker, DateValue, DatePicker } from '@mantine/dates';
import { getDateRange } from 'utils/date';
import { IQuickRange } from './IQuickRange';
import {
  getRecentRanges,
  addRecentRange,
  RecentRange,
} from 'utils/dateRangeHistory';

import classes from './Dropdown.module.scss';
import '@mantine/dates/styles.css';
import { getInputRangeLabel } from 'pages/reporting/utils';

const MIN_DATE = new Date('2024-03-28');

export type DateRange = {
  from?: Date;
  to?: Date;
};

interface DateRangeDropdownProps {
  onApply: (range: DateRange, customValue?: string) => void;
  onClose: () => void;
  range?: DateRange;
  maxDate?: Date;
  quickRanges: IQuickRange[];
  hideQuickSearch?: boolean;
  disableTime?: boolean;
}

export const DateRangeDropdown = ({
  onApply,
  onClose,
  range,
  maxDate,
  quickRanges,
  hideQuickSearch,
  disableTime = true,
}: DateRangeDropdownProps) => {
  const [fromDate, setFromDate] = useState<DateValue | undefined>(new Date());
  const [toDate, setToDate] = useState<DateValue | undefined>(new Date());
  const [searchQuickRange, setSearchQuickRange] = useState('');
  const [recentRanges, setRecentRanges] = useState(getRecentRanges());
  const [rangeValue, setRangeValue] = useState<[Date | null, Date | null]>([null, null]);

  const filteredRanges = quickRanges.filter((range) =>
    range.label.toLowerCase().includes(searchQuickRange.toLowerCase())
  );

  const handleQuickRangeSelect = (range: IQuickRange) => {
    const { from, to } = getDateRange(range);

    const updated = addRecentRange(
      { from, to },
      getInputRangeLabel({ from, to })
    );
    setRecentRanges(updated);

    onApply({ from, to }, range.customValue);
    onClose();
  };

  const handleRecentRangeSelect = (range: RecentRange) => {
    const from = new Date(range.from || '');
    const to = new Date(range.to || '');

    const updated = addRecentRange({ from, to }, range.label);
    setRecentRanges(updated);

    onApply({ from, to });
    onClose();
  };

  const handleApplyRange = (customValue?: string) => {
    if (fromDate && toDate) {
      let from = fromDate;
      let to = toDate;
      if (disableTime) {
        from = new Date(from);
        from.setHours(0, 0, 0, 0);
        to = new Date(to);
        to.setHours(23, 59, 0, 0);
      }
      const label = getInputRangeLabel({ from, to, disableTime });
      // Add to recent ranges
      const updated = addRecentRange({ from, to }, label);
      setRecentRanges(updated);
      onApply({ from, to }, customValue);
      onClose();
    }
  };

  useEffect(() => {
    if (range) {
      setFromDate(range.from);
      setToDate(range.to);
      setRangeValue([range.from ?? null, range.to ?? null]);
    }
  }, [range]);

  return (
    <Popover.Dropdown className={classes.dropdown}>
      <Flex direction="column" className={classes.container}>
        <Flex
          justify="flex-end"
          w="100%"
          pos="sticky"
          top={0}
          pt="xs"
          bg="white"
          hiddenFrom="sm"
          style={{ zIndex: 1 }}
        >
          <Button
            variant="default"
            mx="sm"
            size="xs"
            my="sm"
            onClick={onClose}
            style={{ alignSelf: 'flex-end' }}
          >
            Close
          </Button>
        </Flex>
        <Flex gap="lg" p="sm" direction={{ base: 'column', sm: 'row' }}>
          <Stack gap="md" w={{ base: '100%', sm: '18rem' }}>
            <div>
              {disableTime ? (
                <DatePicker
                  type="range"
                  value={rangeValue}
                  onChange={(val) => {
                    setRangeValue(val as [Date | null, Date | null]);
                    if (val && val[0] && val[1]) {
                      setFromDate(val[0]);
                      setToDate(val[1]);
                    }
                  }}
                  minDate={MIN_DATE}
                  maxDate={maxDate || new Date()}
                  style={{ marginBottom: 12 }}
                />
              ) : (
                <>
                  <DateTimePicker
                    label="Start"
                    value={fromDate}
                    onChange={(val: Date | null) => setFromDate(val as Date)}
                    minDate={MIN_DATE}
                    maxDate={maxDate || new Date()}
                    style={{ marginBottom: 12 }}
                  />
                  <DateTimePicker
                    label="End"
                    value={toDate}
                    onChange={(val: Date | null) => setToDate(val as Date)}
                    minDate={fromDate || MIN_DATE}
                    maxDate={maxDate || new Date()}
                    style={{ marginBottom: 12 }}
                  />
                </>
              )}
            </div>

            <Group gap="xs">
              <Button variant="default" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => handleApplyRange()}
                disabled={!fromDate || !toDate}
                size="sm"
                flex={1}
              >
                Apply time range
              </Button>
            </Group>

            <Divider />

            <div>
              <Text fw={500} mb="xs" fz="0.925rem">
                Recently used
              </Text>
              <Stack
                gap="0.75rem"
                mt="sm"
                className={classes.recentRangeContainer}
              >
                {recentRanges.map((range: RecentRange) => (
                  <UnstyledButton
                    key={range.timestamp}
                    fz="sm"
                    onClick={() => handleRecentRangeSelect(range)}
                    className={classes.recentRange}
                  >
                    {range.label}
                  </UnstyledButton>
                ))}
                {recentRanges.length === 0 && (
                  <Text c="dimmed" fz="sm">
                    No recent ranges
                  </Text>
                )}
              </Stack>
            </div>
          </Stack>
          <Divider
            orientation="vertical"
            style={{ borderColor: 'var(--mantine-color-gray-2)' }}
          />
          <Stack>
            {!hideQuickSearch && (
            <div>
              <Text fw={500} fz="0.925rem" mb={{ base: 'md', sm: 'xs' }}>
                Quick search
              </Text>
              <Stack gap="xs">
                <Text fw={500} mt="sm" fz="sm" c="transparent" visibleFrom="sm">
                  Quick search
                </Text>
                <TextInput
                  radius="sm"
                  placeholder="Search quick ranges"
                  value={searchQuickRange}
                  size="sm"
                  onChange={(e) => setSearchQuickRange(e.target.value)}
                  mb="sm"
                />
              </Stack>
              <Stack
                gap="0.75rem"
                mt="sm"
                className={classes.quickRangeContainer}
              >
                {filteredRanges.map((range: IQuickRange) => (
                  <UnstyledButton
                    key={`quick-${range.label}`}
                    fz="sm"
                    onClick={() => handleQuickRangeSelect(range)}
                    className={classes.quickRangeButton}
                  >
                    {range.label}
                  </UnstyledButton>
                ))}
              </Stack>
            </div>
            )}
          </Stack>
        </Flex>
      </Flex>
    </Popover.Dropdown>
  );
};
